// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";

import { TrackingEvents, track } from "@src/util/analytics.util";

import { AutopilotEventConflictingAttendeesDocument } from "./new-apollo-queries/__generated__/AutopilotEventConflictingAttendees.generated";
import { Button } from "@clockwise/design-system";
// autopilot-event-history imports
import { ClockwiseHistory } from "@clockwise/web-commons/src/ui/clockwise-history";
import { ExternalLinkButton } from "@clockwise/web-commons/src/ui/external-link-button";
import { RelayProp } from "react-relay";
import { StatusEnum } from "@schema";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { attendeesHaveConflict } from "@components/event-scheduling/EventScheduling";
import { autopilotEventHistoryFragments } from "./AutopilotEventHistory.gql";
import { createFragmentContainer } from "react-relay";
import { debounce } from "lodash";
import { flexMeetingHistoryFeedbackUrl } from "@clockwise/client-commons/src/constants/site";
import { getEventHistoryFromEvent } from "@clockwise/web-commons/src/util/event-history.util";
import { getStartOfNextCycle } from "@src/util/pricing.util";
import { getValue } from "@clockwise/web-commons/src/util/errorable.util";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { setPricingDialogOpen } from "@actions/payments.actions";
import { unpauseEvent, inviteToBillingGroup, sendBillingGroupRequestEmail } from "@mutations";
import { useDispatch } from "react-redux";
import { useIntersectionObserver } from "@src/hooks";
import { useQuery } from "@apollo/client";

/////////////////
// Styles //
/////////////////
const useStyles = makeStyles({
  container: {
    backgroundColor: greys.white,
    display: "flex",
    flexDirection: "column",
    flex: "1 0 auto",

    "& > div": {
      padding: `0px 5px 15px 15px`,
    },
  },
});

interface IContainer {
  event: ISchema.IEvent;
  scrollIntoView: boolean;
  orgAccountingPeriod: ISchema.IOrgAccountingPeriod | null;
  orgId: string;
  calendarId: string;
  onViewAutopilotMeetings: () => void;
  organizerPersonId: string | null;
  currentUserBillingGroup: ISchema.IBillingGroup | null;
}

interface IProps extends IContainer {
  relay: RelayProp;
}

///////////////
// Component //
///////////////
const EVENT_HISTORY_ID = "cw-event-history";
const scrollSelfIntoView = (id: string) => {
  const element = document.getElementById(id);

  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "end", inline: "end" });
  }
};

const OBSERVER_INTERSECTION_THRESHOLD = 0.75;

const AutopilotEventHistoryCmpt = ({
  event,
  scrollIntoView,
  orgAccountingPeriod,
  orgId,
  calendarId,
  onViewAutopilotMeetings,
  organizerPersonId,
  currentUserBillingGroup,
  relay,
}: IProps) => {
  const t = React.useContext(TranslationContext);
  const classes = useStyles();
  const dispatch = useDispatch();

  const { data } = useQuery(AutopilotEventConflictingAttendeesDocument, {
    variables: {
      orgId,
      externalEventId: event.eventKey.externalEventId,
    },
  });
  const org = getValue(data?.org, "Org");
  const currentScheduledSlotImpact = getValue(org?.computeCurrentSlotImpactErrorable);
  const currentSlot = getValue(currentScheduledSlotImpact?.currentSlot);
  const hasConflict = attendeesHaveConflict(currentSlot);

  const eventHistory = getEventHistoryFromEvent(event);
  const formattedNewCycleStartDate = getStartOfNextCycle(orgAccountingPeriod).format("MMM D") || "";
  const eventAutopilotStatus = getValue(event.autopilotEventStatus)?.status || null;

  const canUserInviteOrganizerToPlan = currentUserBillingGroup?.invitePolicy === "AnyMember";

  const [setTarget, entry] = useIntersectionObserver({
    threshold: OBSERVER_INTERSECTION_THRESHOLD,
  });

  const trackAutopilotHistoryEvent = React.useCallback(
    (trackingString: string, properties?: Record<string, unknown>) => {
      track(trackingString, {
        eventTitle: event.title,
        externalEventId: event.eventKey?.externalEventId,
        ...properties,
      });
    },
    [event],
  );

  const debouncedAPHistoryScrollTracking = React.useCallback(
    debounce(
      () =>
        trackAutopilotHistoryEvent(
          TrackingEvents.AUTOPILOT_HISTORY
            .SIDEBPANEL_VIEW_FLEXIBLE_MEETING_HISTORY_SCROLL_THRESHOLD_REACHED,
        ),
      5000,
    ),
    [],
  );

  React.useEffect(() => {
    if (entry.isIntersecting) {
      debouncedAPHistoryScrollTracking();
    }
  }, [entry]);

  React.useEffect(() => {
    if (scrollIntoView) {
      scrollSelfIntoView(EVENT_HISTORY_ID);
    }
  }, [scrollIntoView]);

  // hide event history only if we're missing
  // eventHistory or if the event is private
  if (!eventHistory || eventHistory.status === StatusEnum.AutopilotHistoryNotVisible) {
    return null;
  }

  const onViewPlans = () => {
    dispatch(setPricingDialogOpen(true, undefined));
  };

  const onUnpauseEvent = () => {
    unpauseEvent(
      relay.environment,
      { orgRelayId: orgId, externalEventId: event.eventKey.externalEventId },
      () => null,
      () => null,
      calendarId,
    );

    track(TrackingEvents.DEFRAG.UNPAUSE_AUTOPILOT, {
      externalEventId: event.eventKey.externalEventId,
    });
  };

  const onInviteOrganizerToBillingGroup = (hasPermission: boolean) => {
    if (!currentUserBillingGroup || !currentUserBillingGroup.id || !organizerPersonId) return;
    const billingGroupId = currentUserBillingGroup.id;

    if (hasPermission) {
      inviteToBillingGroup(
        relay.environment,
        {
          orgRelayId: orgId,
          billingGroupId,
          personIds: [organizerPersonId],
        },
        () => null,
        () => null,
      );
    } else {
      sendBillingGroupRequestEmail(
        relay.environment,
        {
          orgRelayId: orgId,
          billingGroupId,
          personId: organizerPersonId,
        },
        () => null,
        () => null,
      );
    }
  };

  return (
    <div className={classes.container} id={EVENT_HISTORY_ID} ref={setTarget}>
      <ClockwiseHistory
        clockwiseHistoryStatus={eventHistory.status}
        eventAutopilotStatus={eventAutopilotStatus}
        planRefreshDate={formattedNewCycleStartDate}
        onUnpauseEvent={onUnpauseEvent}
        onViewPlans={onViewPlans}
        actions={eventHistory.actions}
        trackTimelineExpanded={(payload) =>
          trackAutopilotHistoryEvent(
            TrackingEvents.AUTOPILOT_HISTORY.SIDEPANEL_FLEXIBLE_MEETING_HISTORY_SHOW_MORE,
            payload,
          )
        }
        hasConflict={hasConflict}
        eventOrganizerEmail={event.organizer?.urnValue || ""}
        isUserInPaidPlan={Boolean(currentUserBillingGroup)}
        onInviteOrganizerToBillingGroup={onInviteOrganizerToBillingGroup}
        canUserInviteOrganizerToPlan={canUserInviteOrganizerToPlan}
      />
      <div className="cw-flex cw-flex-col cw-gap-[16px]">
        <ExternalLinkButton
          url={flexMeetingHistoryFeedbackUrl}
          variant="outlined"
          onLinkOpen={() =>
            trackAutopilotHistoryEvent(
              TrackingEvents.AUTOPILOT_HISTORY.SIDEPANEL_FLEXIBLE_MEETING_HISTORY_GIVE_FEEDBACK,
            )
          }
          cw-id="give-feedback-button"
        >
          Give feedback
        </ExternalLinkButton>
        <Button
          onClick={onViewAutopilotMeetings}
          variant="outlined"
          cw-id="flexible-meeting-view-button"
        >
          {t("ap-history:view-meetings-button")}
        </Button>
      </div>
    </div>
  );
};

///////////
// Relay //
///////////
export const AutopilotEventHistory = createFragmentContainer<IContainer>(
  AutopilotEventHistoryCmpt,
  autopilotEventHistoryFragments,
);
